import React, { useCallback, useEffect, useState } from 'react';
import Layout from '../../shared/components/Layout';
import Modal from '../../shared/components/Modal';
import { useAuth } from '../auth/AuthContext';
import { extractErrorMessage } from '../../shared/utils/errors';
import { ExchangeRateRecord, SwapCurrencyFee, SwapSettings } from '../../shared/types';
import {
  getSwapSettings,
  listExchangeRates,
  updateExchangeRate,
  updateSwapSettings,
} from './api';

/**
 * Swap rates and configuration screen.
 *
 * Rates come from the Platform Rate (Finance -> Platform Rate): the average
 * of Binance, ByBit and OKX prices, cached briefly inside the accounting
 * service. The exchange_rates table acts as a durable fallback when all
 * upstreams are unreachable; admins can override a value manually but it will
 * be overwritten on the next successful refresh.
 *
 * The Swap Settings panel below controls the per-currency transaction fee
 * shown to users and the hidden markup applied on top of every market rate.
 * Fees are configured per source currency; a currency without a row charges
 * no fees.
 */
type CurrencyFeeInputs = { fee: string; fixed: string; markup: string };

const EMPTY_FEE_INPUTS: CurrencyFeeInputs = { fee: '', fixed: '', markup: '' };

const toFeeInputs = (
  overrides: SwapSettings['currency_fees']
): Record<string, CurrencyFeeInputs> => {
  const inputs: Record<string, CurrencyFeeInputs> = {};
  if (!overrides || Array.isArray(overrides)) return inputs;
  Object.entries(overrides).forEach(([currency, override]) => {
    inputs[currency.toUpperCase()] = {
      fee: override.transaction_fee_percent != null ? String(override.transaction_fee_percent) : '',
      fixed: override.transaction_fee_fixed_usd != null ? String(override.transaction_fee_fixed_usd) : '',
      markup: override.markup_percent != null ? String(override.markup_percent) : '',
    };
  });
  return inputs;
};

const RatesPage: React.FC = () => {
  const { admin } = useAuth();
  const [rates, setRates] = useState<ExchangeRateRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [editing, setEditing] = useState<ExchangeRateRecord | null>(null);
  const [formValue, setFormValue] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const [settings, setSettings] = useState<SwapSettings | null>(null);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [settingsError, setSettingsError] = useState<string | null>(null);
  const [enabledCurrencies, setEnabledCurrencies] = useState<string[]>([]);
  const [currencyFeeInputs, setCurrencyFeeInputs] = useState<Record<string, CurrencyFeeInputs>>({});
  const [requireVerification, setRequireVerification] = useState(true);
  const [newFeeCurrency, setNewFeeCurrency] = useState<string>('');
  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsBanner, setSettingsBanner] = useState<string | null>(null);

  const canManage =
    admin?.role === 'super_admin' ||
    admin?.backoffice_role === 'administrator' ||
    (admin?.permissions?.includes('swap.manage_rates') ?? false);

  const loadRates = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await listExchangeRates();
      setRates(data.filter((r) => r.currency.toUpperCase() !== 'USD'));
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to load exchange rates'));
      setRates([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadSettings = useCallback(async () => {
    setSettingsLoading(true);
    setSettingsError(null);
    try {
      const data = await getSwapSettings();
      setSettings(data);
      setEnabledCurrencies(data.enabled_currencies ?? []);
      setCurrencyFeeInputs(toFeeInputs(data.currency_fees));
      setRequireVerification(data.require_verification ?? true);
    } catch (err) {
      setSettingsError(extractErrorMessage(err, 'Failed to load swap settings'));
    } finally {
      setSettingsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRates();
    loadSettings();
  }, [loadRates, loadSettings]);

  const openEdit = (rate: ExchangeRateRecord) => {
    setEditing(rate);
    setFormValue(String(rate.usd_rate));
    setActionError(null);
  };

  const closeEdit = () => {
    setEditing(null);
    setFormValue('');
    setActionError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editing) return;
    const parsed = Number(formValue);
    if (!Number.isFinite(parsed) || parsed < 0) {
      setActionError('Rate must be a non-negative number');
      return;
    }
    setIsSubmitting(true);
    setActionError(null);
    try {
      await updateExchangeRate(editing.currency, parsed);
      closeEdit();
      loadRates();
    } catch (err) {
      setActionError(extractErrorMessage(err, 'Failed to update rate'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const parseFeeValue = (raw: string, max: number): number | null => {
    if (raw.trim() === '') return 0;
    const value = Number(raw);
    if (!Number.isFinite(value) || value < 0 || value > max) return null;
    return value;
  };

  const handleSaveSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    setSettingsBanner(null);

    if (enabledCurrencies.length === 0) {
      setSettingsError('Enable at least one swap currency');
      return;
    }

    const currencyFees: Record<string, SwapCurrencyFee> = {};
    for (const [currency, inputs] of Object.entries(currencyFeeInputs)) {
      const fee = parseFeeValue(inputs.fee, 50);
      if (fee === null) {
        setSettingsError(`${currency}: transaction fee must be between 0 and 50`);
        return;
      }
      const fixed = parseFeeValue(inputs.fixed, 100000);
      if (fixed === null) {
        setSettingsError(`${currency}: fixed fee must be between 0 and 100,000`);
        return;
      }
      const markup = parseFeeValue(inputs.markup, 50);
      if (markup === null) {
        setSettingsError(`${currency}: markup must be between 0 and 50`);
        return;
      }
      currencyFees[currency] = {
        transaction_fee_percent: fee,
        transaction_fee_fixed_usd: fixed,
        markup_percent: markup,
      };
    }

    setSavingSettings(true);
    setSettingsError(null);
    try {
      const updated = await updateSwapSettings({
        enabled_currencies: enabledCurrencies,
        currency_fees: currencyFees,
        require_verification: requireVerification,
      });
      setSettings(updated);
      setEnabledCurrencies(updated.enabled_currencies ?? enabledCurrencies);
      setCurrencyFeeInputs(toFeeInputs(updated.currency_fees));
      setRequireVerification(updated.require_verification ?? requireVerification);
      setSettingsBanner('Settings saved');
    } catch (err) {
      setSettingsError(extractErrorMessage(err, 'Failed to save settings'));
    } finally {
      setSavingSettings(false);
    }
  };

  const formatTimestamp = (iso: string | null | undefined) =>
    iso ? new Date(iso).toLocaleString() : '—';

  const candidateCurrencies = Array.from(
    new Set([...rates.map((r) => r.currency), ...enabledCurrencies, ...Object.keys(currencyFeeInputs)])
  )
    .filter((c) => c.toUpperCase() !== 'USD')
    .sort();

  const feeCurrencies = Object.keys(currencyFeeInputs).sort();
  const addableFeeCurrencies = candidateCurrencies.filter((c) => !currencyFeeInputs[c]);
  const enabledWithoutFees = enabledCurrencies.filter((c) => !currencyFeeInputs[c]).sort();

  const toggleCurrency = (currency: string) => {
    setSettingsBanner(null);
    setEnabledCurrencies((prev) =>
      prev.includes(currency) ? prev.filter((c) => c !== currency) : [...prev, currency]
    );
  };

  const setCurrencyFeeField = (currency: string, field: keyof CurrencyFeeInputs, value: string) => {
    setSettingsBanner(null);
    setCurrencyFeeInputs((prev) => ({
      ...prev,
      [currency]: { ...(prev[currency] ?? EMPTY_FEE_INPUTS), [field]: value },
    }));
  };

  const addFeeCurrency = () => {
    const currency = newFeeCurrency.toUpperCase();
    if (!currency || currencyFeeInputs[currency]) return;
    setSettingsBanner(null);
    setCurrencyFeeInputs((prev) => ({ ...prev, [currency]: { ...EMPTY_FEE_INPUTS } }));
    setNewFeeCurrency('');
  };

  const removeFeeCurrency = (currency: string) => {
    setSettingsBanner(null);
    setCurrencyFeeInputs((prev) => {
      const next = { ...prev };
      delete next[currency];
      return next;
    });
  };

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white">Swap Exchange Rates</h1>
            <p className="text-slate-400 mt-1">
              USD rates come from the Platform Rate (average of Binance, ByBit and OKX — see Finance → Platform Rate)
              plus the fee &amp; spread you charge on top.
            </p>
          </div>
          <button
            type="button"
            onClick={() => { loadRates(); loadSettings(); }}
            className="btn btn-secondary py-2"
          >
            Refresh
          </button>
        </div>

        <div className="card p-4 text-sm text-slate-400 leading-relaxed">
          <p>
            The accounting service refreshes prices every ~15 seconds. Each pair rate is computed as
            <span className="font-mono text-slate-300"> usd_rate(from) / usd_rate(to)</span> and then reduced by the
            <span className="text-white font-medium"> hidden markup </span> configured for the source currency. The
            <span className="text-white font-medium"> transaction fee </span> is shown to users and deducted from the
            source amount. Fees are set per currency below — a currency without a row charges no fees.
          </p>
        </div>

        <div className="card p-6 space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-white">Swap Settings</h2>
              <p className="text-slate-400 text-sm mt-1">
                These values are applied on top of the live market rates for every preview and execution.
              </p>
            </div>
            {settings?.updated_at && (
              <p className="text-xs text-slate-500 whitespace-nowrap">
                Updated {formatTimestamp(settings.updated_at)}
              </p>
            )}
          </div>

          {settingsLoading ? (
            <div className="flex items-center gap-2 text-slate-400 text-sm">
              <div className="w-4 h-4 border-2 border-indigo-500/30 border-t-indigo-500 rounded-full animate-spin" />
              Loading settings…
            </div>
          ) : (
            <form onSubmit={handleSaveSettings} className="space-y-5">
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-2">
                Swap currencies
              </label>
              <p className="text-xs text-slate-500 mb-3">
                Currencies users can swap between. Disabled currencies are hidden from the swap UI.
              </p>
              {candidateCurrencies.length === 0 ? (
                <p className="text-sm text-slate-500">No currencies available yet.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {candidateCurrencies.map((currency) => {
                    const active = enabledCurrencies.includes(currency);
                    return (
                      <button
                        key={currency}
                        type="button"
                        onClick={() => toggleCurrency(currency)}
                        disabled={!canManage}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-all disabled:opacity-50 ${
                          active
                            ? 'bg-indigo-500/20 text-indigo-300 border-indigo-500/40'
                            : 'bg-slate-800/40 text-slate-400 border-slate-700 hover:border-slate-500'
                        }`}
                      >
                        {currency}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-400 mb-2">
                Access
              </label>
              <label className="flex items-center gap-3 text-slate-200 text-sm">
                <input
                  type="checkbox"
                  checked={requireVerification}
                  disabled={!canManage}
                  onChange={(e) => { setSettingsBanner(null); setRequireVerification(e.target.checked); }}
                  className="rounded border-slate-700 bg-slate-800 text-indigo-500"
                />
                Require verification for swap (verified email/phone or 2FA)
              </label>
              <p className="text-xs text-slate-500 mt-2">
                When off, any signed-in user can swap. The gateway caches this switch, so changes take effect within about a minute.
              </p>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-400 mb-2">
                Per-currency fees
              </label>
              <p className="text-xs text-slate-500 mb-3">
                Fees charged when the user swaps <em>from</em> the given currency. Empty fields count
                as 0. A currency without a row charges no fees at all.
              </p>
              {feeCurrencies.length === 0 ? (
                <p className="text-sm text-amber-400 mb-3">
                  No fees configured — all swaps are currently free for the platform's users.
                </p>
              ) : (
                <div className="overflow-x-auto mb-3">
                  <table className="w-full">
                    <thead className="bg-slate-800/50">
                      <tr>
                        <th className="table-header">Currency</th>
                        <th className="table-header">Transaction fee, %</th>
                        <th className="table-header">Fixed fee, USD</th>
                        <th className="table-header">Hidden markup, %</th>
                        {canManage && <th className="table-header text-right">Actions</th>}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800">
                      {feeCurrencies.map((currency) => {
                        const inputs = currencyFeeInputs[currency] ?? EMPTY_FEE_INPUTS;
                        return (
                          <tr key={currency}>
                            <td className="table-cell">
                              <span className="text-white font-semibold">{currency}</span>
                            </td>
                            <td className="table-cell">
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                max="50"
                                value={inputs.fee}
                                onChange={(e) => setCurrencyFeeField(currency, 'fee', e.target.value)}
                                placeholder="0"
                                className="input font-mono"
                                disabled={!canManage}
                              />
                            </td>
                            <td className="table-cell">
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                max="100000"
                                value={inputs.fixed}
                                onChange={(e) => setCurrencyFeeField(currency, 'fixed', e.target.value)}
                                placeholder="0"
                                className="input font-mono"
                                disabled={!canManage}
                              />
                            </td>
                            <td className="table-cell">
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                max="50"
                                value={inputs.markup}
                                onChange={(e) => setCurrencyFeeField(currency, 'markup', e.target.value)}
                                placeholder="0"
                                className="input font-mono"
                                disabled={!canManage}
                              />
                            </td>
                            {canManage && (
                              <td className="table-cell text-right">
                                <button
                                  type="button"
                                  onClick={() => removeFeeCurrency(currency)}
                                  className="px-3 py-1.5 text-xs font-medium text-red-400 hover:text-red-300 bg-red-500/10 hover:bg-red-500/20 rounded-lg transition-all"
                                >
                                  Remove
                                </button>
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {enabledWithoutFees.length > 0 && (
                <p className="text-xs text-amber-400 mb-3">
                  Enabled currencies without fees (swaps from them are free):{' '}
                  {enabledWithoutFees.join(', ')}
                </p>
              )}

              {canManage && (
                <div className="flex items-center gap-2">
                  <select
                    value={newFeeCurrency}
                    onChange={(e) => setNewFeeCurrency(e.target.value)}
                    className="input w-40"
                    disabled={addableFeeCurrencies.length === 0}
                  >
                    <option value="">Select currency…</option>
                    {addableFeeCurrencies.map((currency) => (
                      <option key={currency} value={currency}>
                        {currency}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={addFeeCurrency}
                    disabled={!newFeeCurrency}
                    className="btn btn-secondary py-2 disabled:opacity-50"
                  >
                    Add currency
                  </button>
                </div>
              )}
            </div>

            <div className="flex flex-wrap items-center justify-end gap-3 pt-1">
              {(settingsError || settingsBanner) && (
                <div className="mr-auto">
                  {settingsError && (
                    <p className="text-red-400 text-xs">{settingsError}</p>
                  )}
                  {settingsBanner && (
                    <p className="text-green-400 text-xs">{settingsBanner}</p>
                  )}
                </div>
              )}
              <button
                type="submit"
                disabled={!canManage || savingSettings}
                className="btn btn-primary disabled:opacity-50"
              >
                {savingSettings ? 'Saving…' : 'Save settings'}
              </button>
            </div>
            </form>
          )}
        </div>

        <div className="card overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-12 h-12 border-4 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin" />
            </div>
          ) : error ? (
            <div className="p-6 text-center">
              <p className="text-red-400">{error}</p>
            </div>
          ) : rates.length === 0 ? (
            <div className="p-12 text-center text-slate-400">No exchange rates configured.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-800/50">
                  <tr>
                    <th className="table-header">Currency</th>
                    <th className="table-header text-right">Platform rate</th>
                    <th className="table-header">Source</th>
                    <th className="table-header">Last fetched</th>
                    {canManage && <th className="table-header text-right">Actions</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {rates.map((rate) => {
                    const market = rate.market_usd_rate != null ? Number(rate.market_usd_rate) : null;
                    const isLive = market !== null && Number.isFinite(market);
                    const platformRate = isLive ? market : Number(rate.usd_rate);
                    return (
                      <tr key={rate.id} className="hover:bg-slate-800/30 transition-colors">
                        <td className="table-cell">
                          <span className="text-white font-semibold">{rate.currency}</span>
                        </td>
                        <td className="table-cell text-right font-mono text-slate-100">
                          {platformRate.toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 8,
                          })}{' '}
                          <span className="text-xs text-slate-500">USD</span>
                          {!isLive && (
                            <span className="badge bg-amber-500/10 text-amber-300 border border-amber-500/30 ml-2">
                              cached fallback
                            </span>
                          )}
                        </td>
                        <td className="table-cell">
                          {isLive && rate.market_source ? (
                            <span className="badge bg-slate-700/40 text-slate-300 border border-slate-600 capitalize">
                              {rate.market_source}
                            </span>
                          ) : (
                            <span className="text-slate-500">—</span>
                          )}
                        </td>
                        <td className="table-cell text-slate-400">
                          {formatTimestamp(isLive ? rate.market_fetched_at ?? rate.updated_at : rate.updated_at)}
                        </td>
                        {canManage && (
                          <td className="table-cell text-right">
                            <button
                              onClick={() => openEdit(rate)}
                              className="px-3 py-1.5 text-xs font-medium text-indigo-400 hover:text-indigo-300 bg-indigo-500/10 hover:bg-indigo-500/20 rounded-lg transition-all"
                            >
                              Override rate
                            </button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <Modal
        isOpen={editing !== null}
        onClose={closeEdit}
        title={editing ? `Override rate for ${editing.currency}` : ''}
      >
        {editing && (
          <form onSubmit={handleSubmit} className="space-y-4">
            {actionError && (
              <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                {actionError}
              </div>
            )}
            <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-amber-300 text-xs">
              This override is temporary. The next successful market refresh (~15s) will
              replace this value with the live price.
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                {editing.currency} = ? USD
              </label>
              <input
                type="number"
                step="0.00000001"
                min="0"
                value={formValue}
                onChange={(e) => setFormValue(e.target.value)}
                className="input font-mono"
                required
              />
              <p className="text-xs text-slate-500 mt-1">
                Used as the durable fallback if all platform rate sources (Binance, ByBit, OKX) fail.
              </p>
            </div>
            <div className="flex gap-3 pt-4">
              <button type="button" onClick={closeEdit} className="flex-1 btn btn-secondary">
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 btn btn-primary disabled:opacity-50"
              >
                {isSubmitting ? 'Saving…' : 'Save fallback rate'}
              </button>
            </div>
          </form>
        )}
      </Modal>
    </Layout>
  );
};

export default RatesPage;
